import { useEffect, useState, useCallback, useRef } from 'react'
import { typeidUnboxed } from 'typeid-js'
import { i18n } from '@/i18n'
import { useAuthState } from '@/lib/auth-state'
import { orpc, toApiUrl } from '@/lib/orpc-client'
import { acquireChat, releaseChat } from '@/lib/chat/chat-pool'
import type { ChatClient } from '@/lib/chat/chat-client'
import { busySinceFor } from '@/lib/chat/slow-turn'
import {
  type AgentArtifact,
  type AgentStatus,
  type AgentStep,
  type ChatAttachment,
  type ChatMessage,
  type ChatMessageErrorKind,
  type MessageCitation,
  type MessageUsage,
  ACTIVE_THREAD_STATES,
} from '@hub/contracts'

export type {
  AgentArtifact,
  AgentStatus,
  AgentStep,
  MessageCitation,
  MessageUsage,
  ChatMessageErrorKind,
  ChatMessage,
}

/** @deprecated legacy alias — use ChatAttachment from @hub/contracts. */
export type ChatAttachmentInfo = ChatAttachment

/** A message typed while the agent was busy — local to this tab until sent. */
export interface QueuedMessage {
  id: string
  content: string
  attachments?: ChatAttachment[]
  createdAt: string
  userId: string
  userName: string
  userAvatarUrl?: string
}

export interface TypingUser {
  userId: string
  name: string
}

/** Busy agent status older than this is shown as idle (see the status effect). */
const AGENT_STATUS_STALE_MS = 10 * 60_000

/**
 * One chat thread, live (PLAN-082): messages, agent status and typing from
 * the thread's ChatStore over its socket (pooled + cached, so revisits
 * render instantly). Writes go through `chat.*` procedures; this hook
 * mirrors them locally right away.
 */
export function useChatCollab(projectId: string, threadId: string) {
  const { me } = useAuthState()
  const [client, setClient] = useState<ChatClient | null>(null)
  const [baseMessages, setMessages] = useState<ChatMessage[]>([])
  const [typingUsers, setTypingUsers] = useState<TypingUser[]>([])
  const [rawStatus, setRawStatus] = useState<AgentStatus>({ state: 'idle' })
  const [agentStatus, setAgentStatus] = useState<AgentStatus>({ state: 'idle' })
  /** Assistant message whose turn went stale (client-side interrupted view). */
  const [staleMessageId, setStaleMessageId] = useState<string | null>(null)
  const [synced, setSynced] = useState(false)
  const [ready, setReady] = useState(false)
  const [messageQueue, setMessageQueue] = useState<QueuedMessage[]>([])
  const [hasOlderMessages, setHasOlderMessages] = useState(false)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const meRef = useRef(me)
  meRef.current = me

  useEffect(() => {
    const c = acquireChat(projectId, threadId)
    setClient(c)
    setMessages(c.messages)
    setReady(c.ready)
    setSynced(c.synced)
    setRawStatus(c.agentStatus)
    setHasOlderMessages(c.hasMore)
    setLoadingOlder(c.loadingHistory)
    setMessageQueue([])
    setStaleMessageId(null)
    const offs = [
      c.on('messages', (list) => {
        setMessages(list)
        setReady(true)
        setSynced(c.synced)
      }),
      c.on('status', setRawStatus),
      c.on('typing', (users) => {
        const selfId = meRef.current?.user.id
        const next = users
          .filter((u) => u.userId !== selfId)
          .map((u) => ({ userId: u.userId, name: u.name || i18n.t('chat:collab.unknownUser') }))
        setTypingUsers((prev) =>
          prev.length === next.length &&
          prev.every((p, i) => p.userId === next[i].userId && p.name === next[i].name)
            ? prev
            : next
        )
      }),
      c.on('history', (hasMore, loading) => {
        setHasOlderMessages(hasMore)
        setLoadingOlder(loading)
      }),
    ]
    return () => {
      for (const off of offs) off()
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
      c.setTyping(false)
      releaseChat(projectId, threadId)
      setClient(null)
    }
  }, [projectId, threadId])

  // A busy status the bridge never closed out would lock the composer; the
  // store refreshes updatedAt many times a minute while working, and marks
  // the turn interrupted itself after 10 min — this is the client-side view
  // of the same rule for the gap in between.
  useEffect(() => {
    let staleTimer: ReturnType<typeof setTimeout> | null = null
    const evaluate = () => {
      let state = rawStatus.state
      let stale = false
      if (ACTIVE_THREAD_STATES.has(state) && rawStatus.updatedAt) {
        const age = Date.now() - Date.parse(rawStatus.updatedAt)
        if (age > AGENT_STATUS_STALE_MS) {
          state = 'idle'
          stale = true
        } else staleTimer = setTimeout(evaluate, AGENT_STATUS_STALE_MS - age + 50)
      }
      setStaleMessageId(stale && rawStatus.messageId ? rawStatus.messageId : null)
      setAgentStatus({ ...rawStatus, state })
    }
    evaluate()
    return () => {
      if (staleTimer) clearTimeout(staleTimer)
    }
  }, [rawStatus])

  /**
   * Echo the user's message right away and return the id the caller must
   * pass to `chat.triggerAgent` (which stores it under that id). The thread
   * is marked busy locally at the same time: the store's `queued` status
   * arrives a round-trip later, and a second send inside that window (a
   * dictation auto-send followed by a manual one) would otherwise trigger a
   * second turn instead of joining the queue.
   */
  const sendMessage = useCallback(
    (content: string, attachments?: ChatAttachment[]): string => {
      const id = typeidUnboxed('msg')
      const user = meRef.current
      if (client && user) {
        client.addOptimistic({
          id,
          role: 'user',
          content,
          threadId,
          createdAt: new Date().toISOString(),
          userId: user.user.id,
          userName: user.user.name ?? undefined,
          userAvatarUrl: user.user.avatarUrl ? toApiUrl(user.user.avatarUrl) : undefined,
          attachments: attachments?.length ? attachments : undefined,
        })
        if (!ACTIVE_THREAD_STATES.has(client.agentStatus.state)) {
          client.applyLocalStatus({ state: 'queued', updatedAt: new Date().toISOString() })
        }
      }
      return id
    },
    [client, threadId]
  )

  /**
   * Remove the echo of a message whose `triggerAgent` call failed, and
   * release the local busy mark (the store never saw the turn).
   */
  const discardMessage = useCallback(
    (id: string) => {
      if (!client) return
      client.removeOptimistic(id)
      if (client.agentStatus.state === 'queued' && !client.agentStatus.messageId) {
        client.applyLocalStatus({ state: 'idle', updatedAt: new Date().toISOString() })
      }
    },
    [client]
  )

  /** Hard delete (retry removes the failed assistant message). */
  const deleteMessage = useCallback(
    (messageId: string) => {
      client?.applyLocalDelete(messageId, 'hard')
      orpc.chat.deleteMessage({ projectId, threadId, messageId, mode: 'hard' }).catch(console.error)
    },
    [client, projectId, threadId]
  )

  /** The author hides their own message; the bubble stays as a placeholder. */
  const softDeleteMessage = useCallback(
    (messageId: string) => {
      client?.applyLocalDelete(messageId, 'soft', meRef.current?.user.id)
      orpc.chat.deleteMessage({ projectId, threadId, messageId, mode: 'soft' }).catch(console.error)
    },
    [client, projectId, threadId]
  )

  const clearAgentError = useCallback(() => {
    if (!client || client.agentStatus.state !== 'error') return
    client.applyLocalStatus({ state: 'idle', updatedAt: new Date().toISOString() })
    orpc.chat.clearAgentError({ projectId, threadId }).catch(console.error)
  }, [client, projectId, threadId])

  /**
   * Stop the reply in progress (`chat.stopAgent`). No local mirror: the store
   * settles the message and goes idle for every tab over the socket, which
   * is also what re-enables the composer here.
   */
  const stopAgent = useCallback(async () => {
    if (!client || !ACTIVE_THREAD_STATES.has(client.agentStatus.state)) return
    await orpc.chat.stopAgent({ projectId, threadId })
  }, [client, projectId, threadId])

  const enqueueMessage = useCallback((content: string, attachments?: ChatAttachment[]) => {
    const user = meRef.current
    if (!user) return
    setMessageQueue((prev) => [
      ...prev,
      {
        id: typeidUnboxed('qmsg'),
        content,
        attachments: attachments?.length ? attachments : undefined,
        createdAt: new Date().toISOString(),
        userId: user.user.id,
        userName: user.user.name ?? '',
        userAvatarUrl: user.user.avatarUrl ? toApiUrl(user.user.avatarUrl) : undefined,
      },
    ])
  }, [])

  const removeQueuedMessage = useCallback((id: string) => {
    setMessageQueue((prev) => prev.filter((m) => m.id !== id))
  }, [])

  const queueRef = useRef(messageQueue)
  queueRef.current = messageQueue
  const drainQueue = useCallback((): QueuedMessage[] => {
    const items = queueRef.current
    if (items.length === 0) return []
    setMessageQueue([])
    return items
  }, [])

  const setTyping = useCallback(
    (isTyping: boolean) => {
      if (!client) return
      client.setTyping(isTyping)
      // Auto-clear after 3 s of inactivity; every call resets the timer.
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
      if (isTyping) typingTimeoutRef.current = setTimeout(() => client.setTyping(false), 3000)
    },
    [client]
  )

  const loadOlderMessages = useCallback(() => {
    client?.loadOlder()
  }, [client])

  // A turn that stopped reporting progress renders as interrupted (with
  // retry) instead of an unfinished-looking reply.
  const messages = staleMessageId
    ? baseMessages.map((m) =>
        m.id === staleMessageId && !m.error && !m.completedAt
          ? { ...m, error: { kind: 'interrupted' as const, retryable: true } }
          : m
      )
    : baseMessages

  /**
   * When the run in progress actually started: the assistant placeholder's
   * `createdAt`, which is server time and the same source the 45 s "taking
   * longer than usual" hint uses.
   *
   * Not a client-side mark on the transition into an active state — a tab
   * opened or reloaded eleven minutes into a run never saw that transition and
   * would think the run was seconds old, so Stop would fire instantly. That is
   * precisely the mistake the confirmation exists to prevent. Null until the
   * placeholder lands (a run that young stops without asking, which is right).
   */
  const agentBusySince = busySinceFor(agentStatus, messages, (state) =>
    ACTIVE_THREAD_STATES.has(state as AgentStatus['state'])
  )

  return {
    messages,
    sendMessage,
    discardMessage,
    deleteMessage,
    softDeleteMessage,
    clearAgentError,
    stopAgent,
    typingUsers,
    setTyping,
    synced,
    ready,
    agentStatus,
    agentBusySince,
    messageQueue,
    enqueueMessage,
    removeQueuedMessage,
    drainQueue,
    hasOlderMessages,
    loadingOlder,
    loadOlderMessages,
  }
}
